/**
 * Single source of truth for internal href normalisation.
 *
 * Every internal link rendered by the app should pass through
 * `resolveInternalHref()` so authority flows straight to the destination URL
 * instead of hopping through a redirect:
 *
 *   1. Absolute canonical-host URLs (`https://www.statlign.com/x`, `http://…`)
 *      become root-relative (`/x`) — no host/scheme 301 in the internal graph.
 *   2. Known aliases resolve to their canonical route
 *      (`/teams/:slug` → `/clubs/:slug`, `/predictions/sure-wins-today` →
 *      `/predictions/bonus-picks-today`, `/safe-bet` → `/safe-bets`).
 *   3. Trailing and duplicated slashes are stripped.
 *   4. An active locale prefix is preserved, so localized pages keep linking
 *      inside their own locale tree.
 *
 * `scripts/verify-no-redirect-links.ts` enforces this against the prerendered
 * HTML, so a redirect-bound internal link fails the build.
 */

import { LANDING_ALIASES } from "@/lib/predictionLandings";
import { LOCALES } from "@/lib/i18n/locales";


/** Hosts that belong to us — links to these should be root-relative. */
export const INTERNAL_HOSTS = [
  "statlign.com",
  "www.statlign.com",
  "statlign.lovable.app",
];

/**
 * Static path aliases → canonical destination. Keep this list in sync with any
 * server-side redirect so the two never disagree.
 */
export const PATH_ALIASES: Record<string, string> = {
  "/safe-bet": "/safe-bets",
  "/safe-bets-today": "/safe-bets",
  "/us-sports": "/nfl-predictions",
  "/sure-wins": "/predictions/bonus-picks-today",
  "/high-odds-predictions": "/high-odds",
  "/predictions/sure-wins-today": "/predictions/bonus-picks-today",
};

/** Locale URL prefixes actually mounted by the app (English lives at root). */
const LOCALE_PREFIXES = LOCALES.filter((l) => l.enabled && l.code !== "en").map((l) => `/${l.code}`);

/** Split a path into its locale prefix (if any) and the rest. */
function splitLocale(path: string): { prefix: string; rest: string } {
  const lower = path.toLowerCase();
  const prefix = LOCALE_PREFIXES.find((p) => lower === p || lower.startsWith(p + "/"));
  if (!prefix) return { prefix: "", rest: path };
  const rest = path.slice(prefix.length) || "/";
  return { prefix, rest };
}


/** Canonicalise the locale-less portion of a path. */
export function canonicalPath(path: string): string {
  let p = path.replace(/\/{2,}/g, "/");
  if (p.length > 1) p = p.replace(/\/+$/, "");
  if (!p) p = "/";

  if (PATH_ALIASES[p]) p = PATH_ALIASES[p]!;

  // /teams/:slug is an alias surface for the club entity.
  const team = p.match(/^\/teams\/([^/]+)$/);
  if (team) p = `/clubs/${team[1]}`;

  // Prediction landing aliases (e.g. sure-wins-today → bonus-picks-today).
  const landing = p.match(/^\/predictions\/([^/]+)$/);
  if (landing && LANDING_ALIASES[landing[1]!]) {
    p = `/predictions/${LANDING_ALIASES[landing[1]!]}`;
  }

  return p;
}

/**
 * Normalise any internal href. External links, mailto/tel/hash-only links and
 * anything non-navigational are returned untouched.
 */
export function resolveInternalHref(href: string): string {
  if (!href) return href;
  const raw = href.trim();

  if (/^(mailto:|tel:|javascript:|data:|#)/i.test(raw)) return raw;

  let working = raw;

  // Absolute or protocol-relative URL on one of our own hosts → relative path.
  const abs = working.match(/^(?:https?:)?\/\/([^/]+)(\/[^\s]*)?$/i);
  if (abs) {
    const host = abs[1]!.toLowerCase();
    if (!INTERNAL_HOSTS.includes(host)) return raw; // genuinely external
    working = abs[2] || "/";
  }

  if (!working.startsWith("/")) return raw;

  // Preserve query string and hash; only the path is normalised.
  const hashIdx = working.indexOf("#");
  const hash = hashIdx >= 0 ? working.slice(hashIdx) : "";
  const noHash = hashIdx >= 0 ? working.slice(0, hashIdx) : working;
  const qIdx = noHash.indexOf("?");
  const query = qIdx >= 0 ? noHash.slice(qIdx) : "";
  const pathOnly = qIdx >= 0 ? noHash.slice(0, qIdx) : noHash;

  const { prefix, rest } = splitLocale(pathOnly);
  const canonical = canonicalPath(rest);
  const joined = prefix ? (canonical === "/" ? prefix : `${prefix}${canonical}`) : canonical;

  return `${joined}${query}${hash}`;
}

/** True when the href would have gone through a redirect before normalisation. */
export function isRedirectBound(href: string): boolean {
  const resolved = resolveInternalHref(href);
  return resolved !== href.trim();
}
